/**
 * `useThreadLoader` — loads the messages + title for a given chat id
 * from `/api/ai/threads/:id`. The hook owns:
 *
 *   - `loading`         — true while the request is in flight
 *   - `notFound`        — true when the server returns 404 (e.g. the
 *                         freshly-minted UUID before its first turn)
 *   - `initialMessages` — the v5 UIMessage[] to hydrate `useChat` with
 *   - `initialTitle`    — the persisted thread title, or null while
 *                         we're still waiting for the first response
 *
 * The orchestrator passes `initialMessages` straight into
 * `useChat({ messages })` so the Chat instance is constructed with
 * the loaded transcript already in place. This avoids the
 * `setMessages-after-id-change` race that mutates an about-to-be-
 * discarded Chat instance.
 */
import { useEffect, useState } from "react";
import type { UIMessage } from "ai";
import { WEB_ENV } from "../../../lib/env.ts";
import type { ThreadResponse } from "../types.ts";

export interface UseThreadLoaderState {
  loading: boolean;
  notFound: boolean;
  initialMessages: UIMessage[];
  initialTitle: string | null;
  initialIcon: string | null;
}

export function useThreadLoader(chatId: string): UseThreadLoaderState {
  const [state, setState] = useState<UseThreadLoaderState>({
    loading: true,
    notFound: false,
    initialMessages: [],
    initialTitle: null,
    initialIcon: null,
  });

  useEffect(() => {
    let cancelled = false;
    setState({
      loading: true,
      notFound: false,
      initialMessages: [],
      initialTitle: null,
      initialIcon: null,
    });

    (async () => {
      try {
        const response = await fetch(`${WEB_ENV.VITE_FLINT_API_URL}/api/ai/threads/${chatId}`, {
          credentials: "include",
        });
        if (cancelled) return;
        if (response.status === 404) {
          setState({
            loading: false,
            notFound: true,
            initialMessages: [],
            initialTitle: null,
            initialIcon: null,
          });
          return;
        }
        if (!response.ok) {
          setState({
            loading: false,
            notFound: false,
            initialMessages: [],
            initialTitle: null,
            initialIcon: null,
          });
          return;
        }
        const data = (await response.json()) as ThreadResponse;
        if (cancelled) return;
        setState({
          loading: false,
          notFound: false,
          initialMessages: data.messages,
          initialTitle: data.thread.title,
          initialIcon: data.thread.icon ?? null,
        });
      } catch {
        if (cancelled) return;
        setState({
          loading: false,
          notFound: false,
          initialMessages: [],
          initialTitle: null,
          initialIcon: null,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chatId]);

  return state;
}
